<template lang="pug">
div(:ref="`block-${this._uid}`" :id="`parallax-${this._uid}`" :class="[sectionClass]" :style="{ height: this.sectionHeight, overflow: this.overflow ? 'hidden' : 'visible', background: this.debug ? '#ffcccc55' : 'transparent' }")
  div(:ref="`parallax-${this._uid}`" :class="[parallax ? parallaxClass : '', fixed ? fixedClass : '', containerClass]" :style="{background: this.debug ? '#ccccff55' : 'transparent'}")
    slot
</template>

<script>
import Vue from 'vue'

// quick in and out ---  :animateOut="true" :delay="300" :speedFactor="1"
// appear fixed fade in-out --- direction="down" :animateOut="true" :delay="-100" :speedFactor="0.6"

export default {
  props: {
    debug: {
      default: false,
      type: Boolean
    },
    update: {
      default: true,
      type: Boolean
    },
    parallax: {
      default: true,
      type: Boolean
    },
    overflow: {
      default: false,
      type: Boolean
    },
    animateOut: {
      default: false,
      type: Boolean
    },
    animateIn: {
      default: true,
      type: Boolean
    },
    speedFactor: {
      default: 0.15,
      type: Number
    },
    scaleFactor: {
      default: 1,
      type: Number
    },
    delay: {
      default: 0, // delay in pixels
      type: Number
    },
    animationTarget: {
      default: () => { return ['background'] }, // accepted values are: position, opacity, rotate, background (image), color
      type: Array
    },
    axis: {
      default: 'vertical', // sets the axis for if animationTarget is "position"
      type: String,
      required: false
    },
    fixed: {
      default: null, // defines the number of pixels the element will remain fixed (i.e. match scrolled pixels) overrides position parallaxing
      type: String,
      required: false
    },
    snap: {
      default: false,
      type: Boolean
    },
    breakpoint: {
      default: '(min-width: 968px)',
      type: String
    },
    height: {
      default: 'auto',
      type: String,
      required: false
    },
    sectionClass: {
      type: String,
      default: 'Masthead'
    },
    containerClass: {
      type: String,
      default: 'Masthead__inner'
    },
    parallaxClass: {
      type: String,
      default: 'is-parallax'
    },
    fixedClass: {
      type: String,
      default: 'is-fixed'
    },
    direction: {
      type: String,
      default: 'up'
    }
  },

  data () {
    return {
      el: null,
      mediaQuery: null,
      sectionHeight: '0px',
      parallaxOffset: 0,
      parentHeight: 0,
      parentOffset: 0,
      parallaxHeight: 0
    }
  },
  mounted () {
    // console.log(this)
    if (this.parallax) {
      this.el = this.$refs[`parallax-${this._uid}`]
      window.requestAnimationFrame = window.requestAnimationFrame ||
      window.mozRequestAnimationFrame ||
      window.webkitRequestAnimationFrame ||
      window.msRequestAnimationFrame ||
      function (f) {
        setTimeout(f, 1000 / 60)
      }
      this.init()
      // console.log('parallax mounted');
    }
  },
  updated () {
    this.init()
    // console.log('parallax updated');
    window.setTimeout(this.init, 1000)
  },
  methods: {
    findPosition (obj) {
      let curleft = 0
      let curtop = 0
      if (obj.offsetParent) {
        do {
          // console.log(obj, obj.offsetTop)
          curleft += obj.offsetLeft
          curtop += obj.offsetTop
        } while (obj = obj.offsetParent) // eslint-disable-line
      }
      return {x: curleft, y: curtop}
    },
    animateElement () {
      // const parentHeight = this.$refs[`block-${this._uid}`].offsetHeight
      // const parentOffset = this.findPosition(this.$refs[`block-${this._uid}`]) // absolute x/y position on page
      // const parallaxHeight = this.$refs[`parallax-${this._uid}`].offsetHeight

      // isInView checks if element is in viewport (before executing this function)
      // available offsets
      const viewportOffset = this.parentOffset.y - window.pageYOffset // gets smaller as element top approaches window top
      const elementOffset = (this.parentHeight - this.parallaxHeight) + this.delay // size difference between container (masthead) and parralaxing inner container (masthead inner)
      let animationValue = (viewportOffset + parseInt(this.delay)) * this.speedFactor // viewportOffset with speed and delay variables etc. applied
      let isPastTop = ((viewportOffset + parseInt(this.delay)) + window.innerHeight) <= 0
      // Conditions for animation
      // default : animationValue should be > 0 and smaller than elementOffset so element stops withing element boundairies
      // if fixed value is set, animation starts when animationValue reaches 0 (negative animation value is converted into positive)
      const inAnimationRange = parseInt(this.fixed) > 0
        ? (animationValue < 0) && ((-animationValue) < parseInt(this.fixed))
        : (animationValue >= 0) && (animationValue < elementOffset)

      this.debug && console.log(
        'inAnimationRange => ', inAnimationRange,
        'fixed => ', parseInt(this.fixed) > 0,
        window.pageYOffset,
        {
          'animationValue': animationValue,
          'viewportOffset': viewportOffset,
          'elementOffset': elementOffset,
          'parentHeight': this.parentHeight,
          'parallaxHeight': this.parallaxHeight,
          'parentOffset': this.parentOffset
        }
      )

      this.animationTarget.forEach((target, i) => {
        switch (target) {
          case 'opacity':
            this.debug && console.log('AT -> opacity', Math.max(0, 1 - (Math.min(1, animationValue / 100))))
            if (inAnimationRange) {
              this.el.style.opacity = this.animateIn ? `${Math.max(0, 1 - (Math.min(1, animationValue / 100)))}` : 1
            } else if (viewportOffset > 0 && !this.animateIn) {
              this.el.style.opacity = 1
            } else {
              if (this.animateOut) {
                this.debug && console.log('animateOut', Math.max(0, 1 + (Math.min(0, animationValue / 50))))
                this.el.style.opacity = animationValue < 0
                  ? Math.max(0, 1 + (Math.min(0, animationValue / 50)))
                  : Math.max(0, 1 - (Math.min(1, animationValue / 100)))
              } else {
                // snap element to end / start state
                // this.debug && console.log('snap');
                this.el.style.opacity = Math.max(0, 1 - (Math.min(1, animationValue / 100)))
              }
            }
            break
          case 'rotate':
            if (inAnimationRange) {
              this.el.style.transform = `rotate(${((animationValue * this.directionValue) / 10)}deg)`
            }
            break
          case 'background':
            if (inAnimationRange) {
              this.el.style.backgroundPosition = `0 ${animationValue * this.directionValue}px`
            }
            break
          case 'color':

            break
          case 'position':
          default:
            // console.log('position - vertical')
            if (this.fixed) {
              if (!inAnimationRange && !isPastTop) {
                this.setStyles(this.el, {
                  position: 'relative',
                  width: 'auto',
                  height: this.fixed + 'px',
                  top: 0,
                  // left: 'auto',
                  transform: 'translate3d(0, 0px,0)'
                })
              } else if (inAnimationRange && isPastTop) { // animationRange only puts it in viewport, so we check if the top is also at/past viewport top
                this.setStyles(this.el, {
                  position: 'fixed',
                  width: this.$refs[`block-${this._uid}`].offsetWidth + 'px',
                  height: this.fixed + 'px',
                  // left: 0,
                  top: 0,
                  // padding: '5vw',
                  transform: 'translate3d(0, 0px,0)'
                })
              } else if (inAnimationRange && (-animationValue) > parseInt(this.fixed)) { // check if element has scrolled the given fixed value
                this.setStyles(this.el, {
                  position: 'relative',
                  width: 'auto',
                  height: this.fixed + 'px',
                  top: 0,
                  // left: 'auto',
                  transform: `translate3d(0, 0px,0)`
                })
              } else if (viewportOffset > 1) {
                this.setStyles(this.el, {
                  position: 'relative',
                  width: 'auto',
                  height: this.fixed + 'px',
                  top: 0,
                  // left: 'auto',
                  transform: `translate3d(0, 0px,0)`
                })
              }
            } else {
              this.debug && console.log('not fixed', inAnimationRange)
              if (this.axis !== 'horizontal') {
                this.debug && console.log('vertical', `${animationValue * this.directionValue}px`)
                if (inAnimationRange) {
                  this.el.style.transform = `translate3d(0, ${animationValue * this.directionValue}px ,0)`
                } else {
                  if (this.animateOut) {
                    // this.debug && console.log('animateOut')
                    if (this.direction === 'up' && animationValue < 0) {
                      this.el.style.transform = `translate3d(0, ${(-animationValue) * this.directionValue}px ,0)`
                    }
                  }
                }
              } else {
                // console.log('position - horizontal')
                if (inAnimationRange) {
                  this.el.style.transform = `translate3d(${((animationValue) * this.directionValue)}px, 0, 0)`
                }
              }
            }
            break
        }
      })
    },
    resetElement () {
      this.animationTarget.forEach((target, i) => {
        switch (target) {
          case 'opacity':
            this.el.style.opacity = 1
            break
          case 'rotate':
            this.el.style.transform = `rotate(${0}deg)`
            break
          case 'background':
            this.el.style.backgroundPosition = `0 ${0}px`
            break
          case 'color':

            break
          case 'position':
          default:
            if (this.axis !== 'horizontal') {
              if (this.directionValue > 0) {
                this.el.style.transform = `translate3d(0, ${0}px ,0)`
              } else {
                this.el.style.transform = `translate3d(0, ${0}px ,0)`
              }
            } else {
              this.el.style.transform = `translate3d(${0}px, 0, 0)`
            }
            break
        }
      })
    },
    scrollHandler () {
      window.requestAnimationFrame(() => {
        // this.debug && console.log('animate', this.isInView(this.$refs[`parallax-${this._uid}`]))
        if (this.isInView(this.$refs[`parallax-${this._uid}`])) {
          this.update && Vue.set(this, 'parentOffset', this.findPosition(this.$refs[`block-${this._uid}`]))

          if (this.$refs[`parallax-${this._uid}`].style.willChange !== 'transform, opacity') this.$refs[`parallax-${this._uid}`].style.willChange = 'transform, opacity'
          this.animateElement()
        } else {
          if (this.$refs[`parallax-${this._uid}`].style.willChange !== 'auto') this.$refs[`parallax-${this._uid}`].style.willChange = 'auto'
          // this.resetElement()
        }
      })
    },

    isInView (el) {
      let rect = el.getBoundingClientRect()
      return (rect.bottom >= 0 && rect.top <= (window.innerHeight || document.documentElement.clientHeight))
    },
    setupListener () {
      window.addEventListener('scroll', this.scrollHandler, false)
      this.setOffset()
      // if (this.mediaQuery.matches) {
      //   window.addEventListener('scroll', this.scrollHandler, false)
      //   this.setOffset()
      //
      // } else {
      //   window.removeEventListener('scroll', this.scrollHandler, false)
      //   // this.resetElement()
      //   Vue.set(this, 'sectionHeight', 'auto')
      //   // console.log(this.mediaQuery.matches);
      // }
    },

    init () {
      this.debug && console.log('init parallax')
      this.mediaQuery = window.matchMedia(this.breakpoint)
      this.fixed
        ? setTimeout(this.setOffset, 1000)
        : this.setOffset()
      // this.resetElement()
      // this.setStyles()

      if (this.mediaQuery) {
        this.mediaQuery.addListener(this.setupListener)
        this.setupListener()
      }
    },
    setOffset () {
      // setTimeout(() => { this.findPosition(this.$refs[`block-${this._uid}`]) }, 5000)
      // console.log('setOffset', this.findPosition(this.$refs[`block-${this._uid}`]))
      const parallaxOffset = (this.$refs[`parallax-${this._uid}`].offsetHeight + parseInt(this.delay)) + ((this.scaleFactor * 1000) * this.speedFactor)
      this.debug && console.log('--->', parallaxOffset, this.$refs[`parallax-${this._uid}`].offsetHeight)
      Vue.set(this, 'parentOffset', this.findPosition(this.$refs[`block-${this._uid}`]))
      Vue.set(this, 'parentHeight', this.$refs[`block-${this._uid}`].offsetHeight)
      Vue.set(this, 'parallaxOffset', parallaxOffset)
      Vue.set(this, 'parallaxHeight', this.$refs[`parallax-${this._uid}`].offsetHeight)
      Vue.set(this, 'sectionHeight',
        `${
          this.fixed
            ? this.fixed + 'px'
            : this.height
              ? this.height === 'auto'
                ? parallaxOffset + 'px'
                : this.height + 'px'
              : parallaxOffset + 'px'
        }`
      )
      this.animateElement()
    },
    setStyles (el, s) {
      Object.assign(el.style, s)
    }
  },

  beforeDestroy () {
    window.removeEventListener('scroll', this.scrollHandler, false)
  },

  computed: {
    directionValue () {
      return this.direction === 'down' ? -1 : +1
    }
  }
}
</script>

<style lang="scss">
@import "~@nextindex/next-scss/next-scss";

.Masthead__inner{
  // transition: opacity .1s ease-in-out;
  // transition-delay: .05s
  -webkit-transform: translate3d(0,0,0);
  transform: translate3d(0,0,0);

  min-height: 100px;

  &.is-fixed{
    min-height: 100vh
  }
}
.Masthead {
  // background: lighten(red, 40%);
  position: relative;
  // min-height: rem(350);
  scroll-behavior: smooth;
  // overflow: hidden;
  // z-index: -1;

  @include has(image) {
    @include media($sm-up) {
      min-height: 100vh;
    }

    width: 100%;
    // overflow: hidden;
    height: 120%;

    > img {
      height: 100%;
      max-width: none;
      width: 100%;
      @include object-fit(cover, top);
    }

    &.is-parallax {
      left: 0;
      position: absolute;
      will-change: transform, opacity;
      right: 0;
      top: 0;

      > img {
        height: 100%;
        max-width: none;
        width: 100%;
        @include object-fit(cover, top);
      }
    }

    &.is-fixed {
      position: fixed;
      will-change: transform;

      > img {
        height: 100vh;
        max-width: none;
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/parallax.vue